import { slugify } from '../lib/slugify';
import { Tooltip, type TooltipValue } from './Tooltip';

export interface ToggleProps {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  description?: string;
  /** v6 — tooltip shown on hover/focus next to the label. */
  tooltip?: TooltipValue;
}

/**
 * Labelled checkbox used for boolean "enable" parameters. Hidden native
 * checkbox + visible toggle pill so the hit target is ≥44px tall.
 */
export function Toggle(props: ToggleProps) {
  const { label, checked, onChange, description, tooltip } = props;
  const id = `toggle-${slugify(label)}`;

  // The toggle's label is a `<span>` inside the `<label>`; we wrap the
  // whole `<label>` with the Tooltip so the hover/focus surface covers
  // both the text and the pill. The Tooltip itself uses inline mode so
  // it does not add an extra tab stop.
  return (
    <Tooltip value={tooltip ?? ''} inline={false}>
      <label
        htmlFor={id}
        className="flex min-h-[44px] cursor-pointer items-center justify-between gap-3 rounded-md px-2 hover:bg-neutral-100 dark:hover:bg-neutral-800/50"
      >
        <span className="text-sm font-medium text-neutral-800 dark:text-neutral-200">{label}</span>
        <span className="relative inline-flex items-center">
          <input
            id={id}
            name={id}
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            aria-label={description ?? label}
            title={description ?? label}
            className="peer sr-only"
          />
          <span
            aria-hidden="true"
            className={[
              'h-7 w-12 rounded-full bg-neutral-300 motion-safe:transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-accent peer-checked:bg-accent dark:bg-neutral-700',
            ].join(' ')}
          />
          <span
            aria-hidden="true"
            className={[
              'absolute left-0.5 h-6 w-6 rounded-full bg-white shadow motion-safe:transition-transform',
              'peer-checked:translate-x-5',
            ].join(' ')}
          />
        </span>
      </label>
    </Tooltip>
  );
}
